// =============================================================================
// R-160 — OCR Health dashboard data hooks. Plain React + setInterval polling,
// mirroring useFleetView's pattern (no react-query in Capture).
// =============================================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api-client";

const POLL_INTERVAL_MS = 15_000;

function useOcrHealthEndpoint<T>(path: string): {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetch = useCallback(async () => {
    setError(null);
    try {
      const result = await api.get<T>(path);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, [path]);

  useEffect(() => {
    fetch();
    intervalRef.current = setInterval(fetch, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

// ── Response types (mirror the route response shapes) ────────────────────────

export interface SourceMixResponse {
  totalFields: number;
  buckets: Array<{ source: string; count: number; percent: number }>;
}

export interface PerSourceAccuracyResponse {
  series: Array<{
    source: string;
    points: Array<{ day: string; total: number; correct: number; accuracy: number }>;
  }>;
}

export interface PriorStatsResponse {
  recentExtractCount: number;
  extractsWithPriorApplied: number;
  pctWithPrior: number;
  avgBoostMagnitude: number;
  profilesWithActivePriors: number;
}

export interface UnresolvedTimelineResponse {
  points: Array<{ day: string; unresolvedCount: number; totalCount: number }>;
}

export interface FieldDrilldownResponse {
  rows: Array<{
    fieldDefinitionId: string;
    fieldName: string;
    perSource: Record<string, { total: number; correct: number; accuracy: number }>;
    lastCorrectionAt: string | null;
  }>;
}

export interface RecentCorrectionsResponse {
  corrections: Array<{
    correctedAt: string;
    documentDisplayId: string | null;
    fieldName: string;
    ocrSource: string | null;
    ocrValue: string | null;
    keyedValue: string | null;
    operatorId: string | null;
    jobTypeName: string | null;
    inkDensityDiag: Record<string, unknown> | null;
  }>;
}

export interface FreshnessResponse {
  lastCorrectionAt: string | null;
  ageSeconds: number;
}

// ── Per-endpoint hooks ───────────────────────────────────────────────────────

export function useSourceMix(days = 30) {
  return useOcrHealthEndpoint<SourceMixResponse>(
    `/admin/ocr-health/source-mix?days=${days}`,
  );
}

export function usePerSourceAccuracy(days = 30) {
  return useOcrHealthEndpoint<PerSourceAccuracyResponse>(
    `/admin/ocr-health/per-source-accuracy?days=${days}`,
  );
}

export function usePriorStats() {
  return useOcrHealthEndpoint<PriorStatsResponse>("/admin/ocr-health/prior-stats");
}

export function useUnresolvedTimeline(days = 30) {
  return useOcrHealthEndpoint<UnresolvedTimelineResponse>(
    `/admin/ocr-health/unresolved-timeline?days=${days}`,
  );
}

export function useFieldDrilldown(jobTypeId?: string) {
  const path = jobTypeId
    ? `/admin/ocr-health/field-drilldown?jobTypeId=${encodeURIComponent(jobTypeId)}`
    : "/admin/ocr-health/field-drilldown";
  return useOcrHealthEndpoint<FieldDrilldownResponse>(path);
}

export function useRecentCorrections(limit = 20) {
  return useOcrHealthEndpoint<RecentCorrectionsResponse>(
    `/admin/ocr-health/recent-corrections?limit=${limit}`,
  );
}

export function useFreshness() {
  return useOcrHealthEndpoint<FreshnessResponse>("/admin/ocr-health/freshness");
}
